import { useEffect, useMemo, useRef } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

export default function LiveMap({
  center,
  destination,
  driverPosition,
  driverHeading,
  followDriver = false,
  autoFocusOnDriver = false,
  routeLineString,
  onMapClick,
  height = 280,
  zoom = 13,
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const destinationMarkerRef = useRef(null);
  const driverMarkerRef = useRef(null);

  const toLngLat = (value) => {
    if (!Array.isArray(value) || value.length !== 2) return null;
    const lng = Number(value[0]);
    const lat = Number(value[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    return [lng, lat];
  };

  const toLineString = (coords) => {
    if (!Array.isArray(coords) || coords.length < 2) return null;
    const cleaned = coords
      .map((c) => toLngLat(c))
      .filter(Boolean);
    return cleaned.length >= 2 ? cleaned : null;
  };

  const distanceSq = (a, b) => {
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    return dx * dx + dy * dy;
  };

  const closestPointOnSegment = (p, a, b) => {
    const abx = b[0] - a[0];
    const aby = b[1] - a[1];
    const apx = p[0] - a[0];
    const apy = p[1] - a[1];
    const abLenSq = abx * abx + aby * aby;
    if (!Number.isFinite(abLenSq) || abLenSq <= 0) {
      return { point: a, t: 0 };
    }
    const t = (apx * abx + apy * aby) / abLenSq;
    const clamped = Math.max(0, Math.min(1, t));
    return {
      point: [a[0] + clamped * abx, a[1] + clamped * aby],
      t: clamped,
    };
  };

  const trimmedRouteLineString = useMemo(() => {
    const cleaned = toLineString(routeLineString);
    const driver = toLngLat(driverPosition);
    if (!cleaned || !driver) return cleaned;

    // Find closest point on any segment (more stable than nearest vertex).
    let bestSegIdx = 0;
    let best = Number.POSITIVE_INFINITY;
    let bestPoint = cleaned[0];

    for (let i = 0; i < cleaned.length - 1; i++) {
      const a = cleaned[i];
      const b = cleaned[i + 1];
      const hit = closestPointOnSegment(driver, a, b);
      const d = distanceSq(hit.point, driver);
      if (d < best) {
        best = d;
        bestSegIdx = i;
        bestPoint = hit.point;
      }
    }

    const tail = cleaned.slice(bestSegIdx + 1);
    if (tail.length < 1) return cleaned;
    return [bestPoint, ...tail];
  }, [routeLineString, driverPosition]);

  const createCarEl = (heading) => {
    const el = document.createElement("div");
    el.style.width = "34px";
    el.style.height = "34px";
    el.style.display = "flex";
    el.style.alignItems = "center";
    el.style.justifyContent = "center";
    el.style.transformOrigin = "center";
    el.style.willChange = "transform";

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 64 64");
    svg.setAttribute("width", "34");
    svg.setAttribute("height", "34");

    const body = document.createElementNS("http://www.w3.org/2000/svg", "path");
    body.setAttribute(
      "d",
      "M16 36h32l4 10v8a2 2 0 0 1-2 2h-4a6 6 0 0 1-12 0H30a6 6 0 0 1-12 0h-4a2 2 0 0 1-2-2v-8l4-10zm6-16h20l6 16H16l6-16zm2 4-3 8h22l-3-8H24z",
    );
    body.setAttribute("fill", "#10b981");
    body.setAttribute("stroke", "#064e3b");
    body.setAttribute("stroke-width", "2");
    body.setAttribute("stroke-linejoin", "round");

    const windowPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
    windowPath.setAttribute("d", "M26 26h12l2 6H24l2-6z");
    windowPath.setAttribute("fill", "#dbeafe");
    windowPath.setAttribute("stroke", "#1e3a8a");
    windowPath.setAttribute("stroke-width", "1.5");

    svg.appendChild(body);
    svg.appendChild(windowPath);
    el.appendChild(svg);

    const h = Number(heading);
    const deg = Number.isFinite(h) ? h : 0;
    el.style.transform = `translate(-50%, -50%) rotate(${deg}deg)`;

    return el;
  };

  const setCarHeading = (marker, heading) => {
    if (!marker) return;
    const el = marker.getElement?.();
    if (!el) return;
    const h = Number(heading);
    const deg = Number.isFinite(h) ? h : 0;
    el.style.transform = `translate(-50%, -50%) rotate(${deg}deg)`;
  };

  const ensureRouteLayer = (map, lineString) => {
    const cleaned = toLineString(lineString);
    if (!cleaned) return;

    const geojson = {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: cleaned,
      },
    };

    const src = map.getSource("route");
    if (!src) {
      map.addSource("route", {
        type: "geojson",
        data: geojson,
      });

      if (!map.getLayer("route-line")) {
        map.addLayer({
          id: "route-line",
          type: "line",
          source: "route",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#2563eb",
            "line-width": 5,
            "line-opacity": 0.9,
          },
        });
      }
    } else {
      try {
        src.setData(geojson);
      } catch (_) {
        // ignore
      }
    }
  };

  const fitRouteBounds = (map, lineString) => {
    const cleaned = toLineString(lineString);
    if (!cleaned) return;
    const bounds = new maplibregl.LngLatBounds();
    cleaned.forEach((c) => {
      bounds.extend(c);
    });
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 40, duration: 0 });
    }
  };

  useEffect(() => {
    const apiKey = process.env.REACT_APP_AWS_LOCATION_API_KEY;
    const region = process.env.REACT_APP_AWS_REGION;

    if (!apiKey || !region) {
      return;
    }

    if (!mapContainerRef.current) return;

    const styleUrl = `https://maps.geo.${region}.amazonaws.com/v2/styles/Standard/descriptor?key=${apiKey}`;

    const initialCenter = toLngLat(center) || [28.0473, -26.2041];

    let cancelled = false;
    const createMap = (style) => {
      if (cancelled) return;
      const map = new maplibregl.Map({
        container: mapContainerRef.current,
        style,
        center: initialCenter,
        zoom,
      });

      map.addControl(new maplibregl.NavigationControl(), "top-right");
      mapRef.current = map;

      map.on("load", () => {
        const dest = toLngLat(destination);
        if (dest) {
          destinationMarkerRef.current = new maplibregl.Marker({ color: "#ef4444" })
            .setLngLat(dest)
            .addTo(map);
        }

        const driver = toLngLat(driverPosition);
        if (driver) {
          driverMarkerRef.current = new maplibregl.Marker({ element: createCarEl(driverHeading) })
            .setLngLat(driver)
            .addTo(map);
        }

        ensureRouteLayer(map, routeLineString);
        if (autoFocusOnDriver && driver) {
          try {
            map.jumpTo({ center: driver });
          } catch (_) {
            // ignore
          }
        }
        if (!(autoFocusOnDriver && driver && followDriver)) {
          fitRouteBounds(map, routeLineString);
        }
      });

      const handleClick = (e) => {
        if (typeof onMapClick !== "function") return;
        const lng = e?.lngLat?.lng;
        const lat = e?.lngLat?.lat;
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
        onMapClick([lng, lat]);
      };

      map.on("click", handleClick);

      return () => {
        map.off("click", handleClick);
        map.remove();
        mapRef.current = null;
      };
    };

    const fallbackStyleUrl = "https://demotiles.maplibre.org/style.json";
    let cleanup = null;
    (async () => {
      try {
        const resp = await fetch(styleUrl);
        const json = await resp.json();
        const hasSources = json && typeof json === "object" && json.sources;
        cleanup = createMap(hasSources ? json : fallbackStyleUrl);
      } catch (_) {
        cleanup = createMap(fallbackStyleUrl);
      }
    })();
    return () => {
      cancelled = true;
      try {
        if (typeof cleanup === "function") cleanup();
      } catch {
        // ignore
      }
    };
  }, []);

  // Update route dynamically (including trimming as driver moves)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const run = () => {
      ensureRouteLayer(map, trimmedRouteLineString);
    };

    if (!map.isStyleLoaded()) {
      map.once("load", run);
      return;
    }

    run();
  }, [trimmedRouteLineString]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (followDriver) return;

    const run = () => {
      fitRouteBounds(map, routeLineString);
    };

    if (!map.isStyleLoaded()) {
      map.once("load", run);
      return;
    }

    run();
  }, [routeLineString, followDriver]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const dest = toLngLat(destination);
    if (!dest) return;

    if (!destinationMarkerRef.current) {
      destinationMarkerRef.current = new maplibregl.Marker({ color: "#ef4444" })
        .setLngLat(dest)
        .addTo(map);
      return;
    }

    try {
      destinationMarkerRef.current.setLngLat(dest);
    } catch (_) {
      // ignore
    }
  }, [destination]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const driver = toLngLat(driverPosition);
    if (!driver) return;

    if (!driverMarkerRef.current) {
      driverMarkerRef.current = new maplibregl.Marker({ element: createCarEl(driverHeading) })
        .setLngLat(driver)
        .addTo(map);
      return;
    }

    try {
      driverMarkerRef.current.setLngLat(driver);
    } catch (_) {
      // ignore
    }
  }, [driverPosition]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!followDriver) return;
    const driver = toLngLat(driverPosition);
    if (!driver) return;

    try {
      map.easeTo({ center: driver, duration: 450 });
    } catch (_) {
      // ignore
    }
  }, [driverPosition, followDriver]);

  useEffect(() => {
    setCarHeading(driverMarkerRef.current, driverHeading);
  }, [driverHeading]);

  return (
    <div
      ref={mapContainerRef}
      style={{ width: "100%", height, borderRadius: 12, overflow: "hidden" }}
    />
  );
}
